import {
	ColumnDef,
	Row,
	SortingState,
	VisibilityState,
} from '@tanstack/react-table';
import useTable from './useTable';
import {
	StyledHeadRow,
	StyledTable,
	StyledTableRow,
	Container,
	ScrollWrapper,
} from './Table.styles';
import { Box, HStack } from '@montel/montelpro-shared-components-v2';
import Th from './Th';
import { NoData } from './NoData';
import TanStackPagination from './Pagination/TanStackPagination';
import Td from './Td';
import { TableHeadArea } from './TableHeadArea';
import { TableContextProvider } from './ColumnFilters/TableContextProvider';
import {
	ActionMenuItem,
	ColumnSizeOptions,
	ExcelExportOptions,
	FilterSettings,
	PaginationOptions,
} from './Table.interfaces';
import ActionMenu from './ActionMenu/ActionMenu';
import React from 'react';
import { ExcelExport } from 'components/common/ExportButton';
import { RequestStatus } from 'types/RequestStatus';
import { TableLoader } from 'components/common/Loader';

interface TableProps<T> {
	tableId?: string;
	columns: ColumnDef<T, any>[];
	requestStatus?: RequestStatus;
	data: T[];
	header?: string;
	headerIcon?: React.ReactNode;
	noDataText?: string;
	withPagination?: boolean;
	withSearch?: boolean;
	paginationOptions?: PaginationOptions;
	columnSizeOptions?: ColumnSizeOptions;
	onRowClicked?: (row: T) => void;
	linkTo?: (row: T) => string;
	initialSorting?: SortingState;
	showRowCount?: boolean;
	filterSettings?: FilterSettings<T>;
	exportOptions?: ExcelExportOptions;
	columnVisibility?: VisibilityState;
	containerGap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	alternateRowBackground?: boolean;
	withExpandableRows?: boolean;
	withSelection?: boolean | ((row: Row<T>) => boolean);
	expandableComponent?: (row: T) => React.ReactNode;
	expandedContent?: (row: T) => React.ReactNode;
	backgroundColor?: string;
	actionMenuItems?: ActionMenuItem<T>[];
}

export function Table<T>(props: TableProps<T>) {
	const {
		tableId,
		columns,
		requestStatus,
		data,
		paginationOptions,
		columnSizeOptions,
		withPagination = false,
		withSearch = false,
		onRowClicked,
		linkTo,
		header,
		headerIcon,
		noDataText = 'No data',
		initialSorting,
		showRowCount = false,
		filterSettings,
		exportOptions,
		containerGap = 'xl',
		padding = 'lg',
		columnVisibility = {},
		alternateRowBackground = false,
		withExpandableRows = false,
		withSelection = false,
		expandableComponent = undefined,
		expandedContent: rowFooterContent = undefined,
		backgroundColor = 'bgMainContainer',
		actionMenuItems,
	} = props;

	const withFilters = !!filterSettings;

	if (requestStatus && requestStatus !== RequestStatus.SUCCESS) {
		return (
			<TableLoader
				withPagination={withPagination}
				withSearch={withSearch}
				withFilters={withFilters}
				status={requestStatus}
			/>
		);
	}

	const { table } = useTable<T>({
		data,
		columns,
		withFilters,
		withSearch,
		withPagination,
		withSelection,
		paginationOptions,
		columnSizeOptions,
		initialSorting,
		columnVisibility,
		withExpandableRows,
		tableId,
	});

	const showHeader =
		withSearch || withFilters || header || headerIcon || showRowCount;

	const shouldShowPagination = (() => {
		if (!withPagination) return false;

		const { pageIndex } = table.getState().pagination;
		const showFromCount = paginationOptions?.showFromCount || 0;
		const hasEnoughRows = table.getRowModel().rows.length >= showFromCount;

		return pageIndex > 0 || hasEnoughRows;
	})();

	const expandedContentColSpan = columns.length + (actionMenuItems ? 1 : 0);

	return (
		<TableContextProvider<T>
			table={table}
			filterSettings={filterSettings}
			data={data}
			tableId={tableId}
		>
			<Container fullWidth gap={containerGap}>
				{showHeader && (
					<TableHeadArea
						header={header}
						headerIcon={headerIcon}
						withSearch={withSearch}
						showRowCount={showRowCount}
					/>
				)}
				<Box px={padding} py={padding} bg={backgroundColor}>
					<ScrollWrapper>
						<StyledTable
							id={tableId}
							$alternateRowBackground={alternateRowBackground}
						>
							<thead>
								{table.getHeaderGroups().map((headerGroup) => (
									<StyledHeadRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<Th
												key={header.id}
												header={header}
											/>
										))}{' '}
										{actionMenuItems && <th />}{' '}
									</StyledHeadRow>
								))}
							</thead>
							<tbody>
								{table.getRowModel().rows.map((row, index) => {
									const rowClassName =
										(index + 1) % 2 === 0 ? 'even' : 'odd';

									const canSelect = withSelection
										? row.getCanSelect()
										: true;

									const clickable = withSelection
										? row.getCanSelect() && !!onRowClicked
										: !!onRowClicked;

									return (
										<React.Fragment key={row.id}>
											<StyledTableRow
												className={rowClassName}
												onClick={() => {
													clickable &&
														onRowClicked?.(
															row.original,
														);
													withExpandableRows &&
														row.toggleExpanded();
													withSelection &&
														row.toggleSelected();
												}}
												$selected={row.getIsSelected()}
												$faded={!canSelect}
												$clickable={
													clickable ||
													withExpandableRows
												}
											>
												{row
													.getVisibleCells()
													.map((cell) => {
														const defaults =
															table._getDefaultColumnDef();
														const href =
															linkTo &&
															!cell.column
																.columnDef.meta
																?.disableRowLink
																? linkTo(
																		row.original,
																  )
																: undefined;
														return (
															<Td
																minWidth={
																	cell.column
																		.columnDef
																		.minSize ??
																	defaults.minSize
																}
																maxWidth={
																	cell.column
																		.columnDef
																		.maxSize ??
																	defaults.maxSize
																}
																key={cell.id}
																cell={cell}
																href={href}
															/>
														);
													})}
												{actionMenuItems && (
													<td>
														<ActionMenu
															row={row.original}
															items={
																actionMenuItems
															}
															unbound
														/>
													</td>
												)}
											</StyledTableRow>
											{rowFooterContent && (
												<StyledTableRow
													onClick={() => {
														clickable &&
															onRowClicked?.(
																row.original,
															);
													}}
													className="expanded-content"
												>
													<td
														colSpan={
															expandedContentColSpan
														}
													>
														{rowFooterContent(
															row.original,
														)}
													</td>
												</StyledTableRow>
											)}
											{row.getIsExpanded() &&
												expandableComponent && (
													<StyledTableRow className="expanded-content">
														<td
															colSpan={
																expandedContentColSpan
															}
														>
															{expandableComponent(
																row.original,
															)}
														</td>
													</StyledTableRow>
												)}
										</React.Fragment>
									);
								})}
							</tbody>
						</StyledTable>
					</ScrollWrapper>
				</Box>
				{table.getRowModel().rows.length === 0 && (
					<NoData>{noDataText}</NoData>
				)}
				{shouldShowPagination && (
					<Box bg="transparent" px="xl" py="0">
						<TanStackPagination table={table} />
					</Box>
				)}
			</Container>

			{exportOptions && (
				<HStack>
					<ExcelExport
						title={exportOptions.buttonTitle}
						documentName={exportOptions.documentName}
						rows={table.getSortedRowModel().rows}
						type={exportOptions.type}
					/>
				</HStack>
			)}
		</TableContextProvider>
	);
}
